import * as React from 'react';
import {
    Edit,
    useEditContext,
    useRedirect,
    useNotify,
    useGetList
} from 'react-admin';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
} from '@mui/material';
import { MemberRole } from '../../enums/MemberRole.ts';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MemberService } from "../../services/member/memberService.ts";
import { VehicleType } from '../../enums/VehicleType.ts';
import AgencyService from '../../services/agency/agencyService.ts';
import CourierService from '../../services/courier/courierService.ts';
import { LocationService } from '../../services/location/locationService.ts';
import { ChangeMemberPasswordDialog } from '../../components/dialogs/ChangeMemberPasswordDialog.tsx';
import CustomerCreditPanel from '../credit/CustomerCreditPanel.tsx';
import CustomerAttractionBookingsPanel from '../attractions/CustomerAttractionBookingsPanel.tsx';
import MemberAcquisitionPanel from '../marketing/MemberAcquisitionPanel.tsx';
import { isAdmin } from '../../core/roles.ts';
import * as Sentry from '@sentry/react';

const MEMBER_CHOICES = Object.entries(MemberRole).map(([id, name]) => ({ id, name }));
const VEHICLE_TYPE_CHOICES = Object.entries(VehicleType).map(([id, name]) => ({ id, name }));
const editContainerSx = {
    '& .MuiCardContent-root': { px: 4, py: 0 },
    '& .RaEdit-main': { mx: 4, my: 0 },
    '& .MuiTableBody-root tr:last-of-type td': { borderBottom: 'none' },
};

const headerTitleSx = {
    paddingLeft: 'var(--space-24)',
    fontWeight: 700,
    fontSize: 'var(--text-xxl)',
};

const tableShellSx = {
    border: '1px solid var(--color-secondary)',
    borderRadius: 'var(--radius-6)',
    overflow: 'hidden',
    width: '100%',
};

const headCellSx = {
    paddingLeft: 'var(--space-24)',
    py: 'var(--space-16)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
};

const bodyCellSx = {
    py: 'var(--space-24)',
    paddingLeft: 'var(--space-24)',
    fontSize: 'var(--text-xs)',
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        border: '1px solid var(--color-secondary)',
        borderRadius: 'var(--radius-6)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
};

const validationSchema = Yup.object({
    role: Yup.string().required('Required'),
    agency: Yup.string(),
    vehicleType: Yup.string(),
    location: Yup.string(),
    name: Yup.string().required('Required'),
    surname: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phone: Yup.string()
        .required('Phone number is required')
        .matches(/^\+[0-9]{2,4}[0-9]{5,12}$/, 'Invalid phone number format'),
});

const MembersEditForm: React.FC = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const { t } = useTranslation();
    const memberService = new MemberService();
    const agencyService = new AgencyService();
    const courierService = new CourierService();
    const locationService = new LocationService();
    const [isCourier, setIsCourier] = React.useState(false);
    const [isVendor, setIsVendor] = React.useState(false);
    const [agencies, setAgencies] = React.useState<any[]>([]);
    const [locations, setLocations] = React.useState<any[]>([]);
    const [openPasswordDialog, setOpenPasswordDialog] = React.useState(false);
    const { record, isLoading } = useEditContext<any>();

    React.useEffect(() => {
        let isActive = true;
        (async () => {
            try {
                const res = await agencyService.getAgencies();
                if (isActive) setAgencies(res);
            } catch (error) {
                notify(error.response.data.error, { type: 'error' });
                console.error(error.response.data.error);
                Sentry.captureException(error);
            }
        })();
        return () => { isActive = false; };
    }, []);

    React.useEffect(() => {
        let isActive = true;
        (async () => {
            try {
                const res = await locationService.getLocations();
                if (isActive) setLocations(res);
            } catch (e: any) {
                notify(e.response?.data?.error ?? 'Failed to load locations', { type: 'error' });
                Sentry.captureException(e);
            }
        })();
        return () => { isActive = false; };
    }, []);

    const _rawRole = record?.role ?? record?.roles?.[0] ?? '';
    const _roleKey =
        _rawRole && Object.prototype.hasOwnProperty.call(MemberRole, _rawRole)
            ? String(_rawRole)
            : (Object.entries(MemberRole).find(([, val]) => String(val) === String(_rawRole))?.[0] ?? '');

    const first = (record?.name ?? '').split(' ')[0];
    const last = (record?.name ?? '').split(' ')[1];

    const {
        data: couriers = [],
        isLoading: isCouriersLoading,
    } = useGetList<any>(
        'commissionCouriers',
        {},
        { enabled: _roleKey === 'COURIER' }
    );
    const courier = _roleKey === 'COURIER'
        ? couriers.find((c: any) => String(c.memberId ?? c.userId ?? c.id) === String(record.id))
        : undefined;

    const initialValues = {
        role: _roleKey,
        agency: agencies.find(a => a?.agencyName?.trim() === courier?.agencyName)?.id ?? '',
        vehicleType: courier?.vehicleType ?? '',
        location: record?.locationId ?? '',
        name: first ?? '',
        surname: last ?? '',
        email: record?.email ?? '',
        phone: record?.phoneNumber ?? record?.phone ?? '',
    };

    React.useEffect(() => {
        setIsCourier(_roleKey === 'COURIER');
        setIsVendor(_roleKey === 'VENDOR');
    });

    if (isLoading || (_roleKey === 'COURIER' && isCouriersLoading)) return null;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        if (values.role === 'COURIER') {
                            const payload: any = {
                                courierId: record.id,
                                phoneNumber: values.phone,
                                name: values.name + ' ' + values.surname,
                                agencyId: values.agency,
                                vehicleType: values.vehicleType
                            };
                            await courierService.updateCourier(payload);
                        }
                        else if (values.role === 'VENDOR') {
                            const payload: any = {
                                id: record.id,
                                phoneNumber: values.phone,
                                name: values.name + ' ' + values.surname,
                                locationId: values.location
                            };
                            await memberService.updateVendor(payload);
                        }
                        else {
                            const payload: any = {
                                id: record.id,
                                phoneNumber: values.phone,
                                name: values.name + ' ' + values.surname
                            };

                            await memberService.updateMember(payload);
                        }
                        notify(t('common.saved'), { type: 'success' });
                        redirect('list', 'members');
                    } catch (e: any) {
                        notify(e.response.data.error, { type: 'error' });
                        console.error(e.response.data.error);
                        Sentry.captureException(e);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                    <Form>
                        <Box
                            sx={{
                                paddingTop: 'var(--space-72)',
                                paddingBottom: 'var(--space-32)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="h5" sx={headerTitleSx}>
                                Members
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                               <Button
                                   style={{
                                       fontSize: 'var(--text-xs)',
                                       backgroundColor: 'transparent',
                                       color: 'var(--color-primary)',
                                       fontWeight: 700,
                                   }}
                                   onClick={() => setOpenPasswordDialog(true)}
                               >
                                   {t('members.changePassword').toLocaleUpperCase()}
                               </Button>
                            </Box>
                        </Box>

                        <Box sx={tableShellSx}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headCellSx}>{t('members.columns.role')}</TableCell>
                                        {isCourier && <>
                                            <TableCell sx={headCellSx}>{t('members.columns.agency')}</TableCell>
                                            <TableCell sx={headCellSx}>{t('members.columns.typeOfVehicle')}</TableCell>
                                        </>
                                        }
                                        {isVendor &&
                                            <TableCell sx={headCellSx}>{t('members.columns.location')}</TableCell>
                                        }
                                        <TableCell sx={headCellSx}>{t('members.columns.name')}</TableCell>
                                        <TableCell sx={headCellSx}>{t('members.columns.surname')}</TableCell>
                                        <TableCell sx={headCellSx}>{t('members.columns.email')}</TableCell>
                                        <TableCell sx={headCellSx}>{t('members.columns.phone')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={bodyCellSx}>
                                            <MuiTextField
                                                select
                                                disabled
                                                name="role"
                                                label=""
                                                sx={{ ...inputSx, width: '150px' }}
                                                size="small"
                                                fullWidth
                                                value={values.role}
                                                onChange={(e) => {
                                                    const role = e.target.value;
                                                    setFieldValue('role', role);
                                                    role === "COURIER" ? setIsCourier(true) : setIsCourier(false);
                                                }}
                                                onBlur={handleBlur}
                                            // helperText={touched.role ? (errors.role || ' ') : ' '}
                                            >
                                                {MEMBER_CHOICES.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </MuiTextField>
                                        </TableCell>
                                        {isCourier && <>
                                            <TableCell sx={bodyCellSx}>
                                                <MuiTextField
                                                    select
                                                    name="agency"
                                                    label=""
                                                    sx={{ ...inputSx, width: '150px' }}
                                                    size="small"
                                                    fullWidth
                                                    value={values.agency}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                //   helperText={touched.agency ? (errors.agency || ' ') : ' '}
                                                >
                                                    {agencies.map((agency: { id: number; agencyName: string }) => (
                                                        <MenuItem key={agency.id} value={agency.id}>
                                                            {agency.agencyName}
                                                        </MenuItem>
                                                    ))}
                                                </MuiTextField>
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <MuiTextField
                                                    select
                                                    name="vehicleType"
                                                    label=""
                                                    sx={inputSx}
                                                    size="small"
                                                    fullWidth
                                                    placeholder={t('members.columns.typeOfVehicle')}
                                                    value={values.vehicleType}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.name && Boolean(errors.name)}
                                                //   helperText={touched.name ? (errors.name || ' ') : ' '}
                                                >
                                                    {VEHICLE_TYPE_CHOICES.map(c => (
                                                        <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                                                    ))}
                                                </MuiTextField>
                                            </TableCell>
                                        </>
                                        }
                                        {isVendor &&
                                            <TableCell sx={bodyCellSx}>
                                                <MuiTextField
                                                    select
                                                    name="location"
                                                    label=""
                                                    sx={{ ...inputSx, width: '150px' }}
                                                    size="small"
                                                    fullWidth
                                                    value={values.location}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                >
                                                    {locations.map((loc: { id: number; name: string }) => (
                                                        <MenuItem key={loc.id} value={loc.id}>
                                                            {loc.name}
                                                        </MenuItem>
                                                    ))}
                                                </MuiTextField>
                                            </TableCell>
                                        }
                                        <TableCell sx={bodyCellSx}>
                                            <MuiTextField
                                                name="name"
                                                label=""
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                placeholder={t('members.columns.name')}
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.name && Boolean(errors.name)}
                                            // helperText={touched.name ? (errors.name || ' ') : ' '}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <MuiTextField
                                                name="surname"
                                                label=""
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                placeholder={t('members.columns.surname')}
                                                value={values.surname}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.surname && Boolean(errors.surname)}
                                            // helperText={touched.surname ? (errors.surname || ' ') : ' '}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <MuiTextField
                                                disabled
                                                name="email"
                                                type="email"
                                                label=""
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                placeholder={t('members.columns.email')}
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.email && Boolean(errors.email)}
                                            // helperText={touched.email ? (errors.email || ' ') : ' '}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <MuiTextField
                                                name="phone"
                                                label=""
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                placeholder={t('members.columns.phone')}
                                                value={values.phone}
                                                onKeyDown={(e) => {
                                                    if (e.key === ' ') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.phone && Boolean(errors.phone)}
                                            // helperText={touched.phone ? (errors.phone || ' ') : ' '}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                                mt: 6,
                                pr: 'var(--space-24)',
                            }}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => redirect('list', 'members')}
                                sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                            >
                                {t('common.cancel')}
                            </Button>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                sx={{
                                    borderRadius: 0,
                                    backgroundColor: 'var(--color-primary)',
                                    color: 'var(--color-text-light)',
                                    boxShadow: 'none',
                                    transition: 'background-color 200ms ease, box-shadow 200ms ease',
                                    '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                }}
                            >
                                {t('common.saveChanges')}
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>

            {/* Customers only: nobody else can hold credit, so nobody else
                gets a panel that would only ever read zero. */}
            {_roleKey === 'CUSTOMER' && <CustomerCreditPanel customerId={record.id} />}

            {/* Attractions this customer said they booked on Viator. Admins only,
                like Attractions › Bookings (the backend enforces the same). */}
            {_roleKey === 'CUSTOMER' && isAdmin() && <CustomerAttractionBookingsPanel customerId={record.id} />}

            {/* Where this customer came from (link, QR code, partner) and every
                consent they gave or withdrew. Admins only; the backend enforces the same. */}
            {_roleKey === 'CUSTOMER' && isAdmin() && <MemberAcquisitionPanel customerId={record.id} />}

            <ChangeMemberPasswordDialog
                open={openPasswordDialog}
                onClose={() => setOpenPasswordDialog(false)}
                userId={record.id}
            />
        </>
    )
};


export const MembersEditTable: React.FC = () => (
    <Edit title="Members" actions={false} sx={editContainerSx}>
        <MembersEditForm />
    </Edit>
);